import { SpinnerPage } from "@/components/loading/spinner.page";
import { ErrorState } from "@/components/ui/error";
import { TitleInputWithLink } from "@/components/ui/title-input-with-link";
import { useAuth } from "@/features/auth";
import { LinkedCrControl } from "@/features/change-requests";
import { chatQueries } from "@/features/chat/api/chat-keys";
import { containerQueries } from "@/features/containers";
import { useRecordContainerAccess } from "@/features/containers/api/container-access-hooks";
import {
  useContainerMemberQuery,
  useContainerMembersQuery,
  useUpdateContainerMutation,
} from "@/features/containers/api/containers-hooks";
import { isMemberType } from "@/features/containers/containers.utils";
import { buildDocsBaseUrl } from "@/features/docs/utils/docs-base-url";
import { FigmaContextScope } from "@/features/figma/figma-context-scope";
import { useProfileQuery } from "@/features/profiles";
import { ContainerMembersAvatarGroup } from "@/features/projects/components/container-members-avatar-group";
import { MEMBER_ACTOR_TYPES } from "@/features/projects/components/container-members-dialog/api/use-manage-members-data";
import { isProjectFeatureEnabled } from "@/features/projects/utils/project-features";
import { ProjectRoute } from "@/routes/_authenticated/workspaces/$slug/projects/$projectSlug";
import * as Sentry from "@sentry/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams, useSearch } from "@tanstack/react-router";
import { useMemo } from "react";
import type { ChangeEvent } from "react";
import {
  useMessagesQuery,
  useMessagesSubscription,
  useRenameMessageRoomMutation,
} from "./api/chat-hooks";
import { getActivePersona } from "./chat-ai-routing";
import {
  getMessageRoomTitle,
  memberRowToParticipant,
  nextMessageRoomName,
  resolveMessageRoomDefaultTitle,
  shouldShowLinkedCrControl,
} from "./chat.utils";
import { RealtimeChat } from "./components/chat";
import { useChatTitleSync } from "./hooks/use-chat-title-sync";
import { useRealtimeChat } from "./hooks/use-realtime-chat";

export default function ChatPage() {
  const queryClient = useQueryClient();

  const { project } = ProjectRoute.useRouteContext();
  const { chatId, slug, projectSlug } = useParams({ strict: false });
  const { messageId, draft } = useSearch({ strict: false });

  const containerId = project.id;
  const roomId = chatId ?? containerId;

  // CR-51: opening an AI chat or a message room is what makes it "recent" in
  // the sidebar. Deliberately `chatId` and not `roomId`: the project fallback
  // above is not a room the user opened, and recording it would put the
  // project itself in a section that only lists rooms.
  useRecordContainerAccess(chatId);
  // Absolute so links the AI surfaces outside the in-chat widget (e.g. Slack
  // posts) point at the real host — relative paths are useless there and the
  // model otherwise invents a bogus domain.
  const docsBaseUrl = buildDocsBaseUrl(slug, projectSlug);
  // CR-126: pasted Figma links get context cards scoped to this project.
  const figmaScope = useMemo(
    () =>
      slug && projectSlug ? { projectId: containerId, workspaceSlug: slug, projectSlug } : null,
    [containerId, slug, projectSlug],
  );

  const { user } = useAuth();
  const userId = user.id;

  const {
    data: containerMember,
    isLoading: isContainerMemberLoading,
    isError: isContainerMemberError,
    error: containerMemberError,
  } = useContainerMemberQuery(userId, roomId);
  const containerMemberId = containerMember?.id;

  // CR-158: only workspace owners/admins can see the credit state and act on
  // it, so only they get the Subscription link on a "no credits left" turn.
  const { data: workspaceMembership } = useContainerMemberQuery(
    userId,
    project.parent_container_id ?? "",
  );
  const canManageCredits =
    workspaceMembership?.role === "owner" || workspaceMembership?.role === "admin";

  const {
    data: roomMembers,
    isLoading: isRoomMembersLoading,
    isError: isRoomMembersError,
    error: roomMembersError,
  } = useContainerMembersQuery(roomId, MEMBER_ACTOR_TYPES);

  const aiMembers = roomMembers?.filter(isMemberType("ai"));

  const {
    data: projectMembers,
    isLoading: isProjectMembersLoading,
    isError: isProjectMembersError,
    error: projectMembersError,
  } = useContainerMembersQuery(containerId, MEMBER_ACTOR_TYPES);

  const projectAiMembers = projectMembers?.filter(isMemberType("ai"));

  const {
    data: profile,
    isLoading: isProfileLoading,
    isError: isProfileError,
    error: profileError,
  } = useProfileQuery(userId);

  const {
    data: roomContainer,
    isLoading: isRoomLoading,
    isError: isRoomError,
    error: roomError,
  } = useQuery({
    ...containerQueries.byId(roomId),
    enabled: !!roomId,
  });

  // A chat can belong to a Change Request (containers.cr_id, CR-27). CR-149:
  // the header renders the shared split control for it, which resolves the CR
  // itself — the page only needs to know whether there is one.
  const linkedCrId = roomContainer?.cr_id ?? null;

  const updateChatName = useUpdateContainerMutation();
  const renameMessageRoom = useRenameMessageRoomMutation();
  // CR-97/CR-199: the hook tracks "did the user type since the title last
  // settled?" (see nextMessageRoomName for why the debounce alone cannot be
  // trusted) and keeps a server rename (auto-naming) from clobbering a title
  // being typed.
  const { title, setTitle, consumeTypedEdit, onTitleChange, onTitleFocus, onTitleBlur } =
    useChatTitleSync(roomContainer?.name, roomId);

  const handleTitleDebounce = (nextTitle: string) => {
    if (roomContainer?.container_type === "message_room") {
      // CR-97: message rooms rename through the membership-gated RPC, and an
      // EMPTY title is a valid write — it clears the stored name so the room
      // shows its derived default again (the only way back to it).
      const name = nextMessageRoomName({
        typed: consumeTypedEdit(nextTitle),
        next: nextTitle,
        stored: roomContainer.name,
      });
      if (name === null) return;
      renameMessageRoom.mutate(
        { roomId, parentContainerId: containerId, name },
        {
          onError: (error) => {
            Sentry.captureException(error);
            setTitle(roomContainer.name ?? "");
          },
        },
      );
      return;
    }
    // The debounce settled the typed value: from here a stored-name change may
    // sync into the header again.
    consumeTypedEdit(nextTitle);
    if (nextTitle !== roomContainer?.name && nextTitle.trim()) {
      updateChatName.mutate(
        {
          containerId: roomId,
          updates: { name: nextTitle.trim() },
        },
        {
          // Keep the AI Chats sidebar list in step with the renamed room
          // (CR-25); message rooms take the branch above.
          onSuccess: () =>
            void queryClient.invalidateQueries({
              queryKey: chatQueries.roomsByParent(containerId).queryKey,
            }),
        },
      );
    }
  };

  // Use cache helpers query for automatic real-time updates
  const {
    data: allMessages,
    isLoading: isAllMessagesLoading,
    isError: isAllMessagesError,
    error: allMessagesError,
  } = useMessagesQuery(roomId, !!roomId);

  useMessagesSubscription(roomId, !!roomId);

  const {
    onSendMessage,
    sendAskUserAnswer,
    isSending,
    isAiResponding,
    sendError,
    resetSendError,
    streamingMessages,
    creditWarning,
    failedAiTurns,
    retryAiTurn,
    isAiTurnActive,
    isStoppingAiTurn,
    onStopAiTurns,
  } = useRealtimeChat({
    containerMember,
    aiPersonas: aiMembers,
    roomId: roomId,
    profile,
    allMessages: allMessages || [],
    projectId: containerId,
    docsBaseUrl,
    workspaceId: project.parent_container_id,
    roomContainerType: roomContainer?.container_type === "message_room" ? "message_room" : "room",
    projectAiPersonas: projectAiMembers,
    isAiShared: roomContainer?.is_ai_shared ?? false,
    defaultAiPersonaId: roomContainer?.default_ai_persona_id ?? null,
  });

  const displayedMessages = useMemo(() => {
    if (!streamingMessages.length) return allMessages;

    // Only merge streams for the active room
    const roomStreaming = streamingMessages.filter(
      (streamingMessage) => streamingMessage.room === roomId, // adjust field name if different
    );

    if (!roomStreaming.length) return allMessages;

    const messageById = new Map(allMessages?.map((message) => [message.id, message]));

    for (const streamingMessage of roomStreaming) {
      messageById.set(streamingMessage.id, streamingMessage);
    }

    return [...messageById.values()];
  }, [allMessages, streamingMessages, roomId]);

  if (
    isProfileLoading ||
    isAllMessagesLoading ||
    isContainerMemberLoading ||
    isRoomMembersLoading ||
    isProjectMembersLoading ||
    isRoomLoading
  ) {
    return <SpinnerPage />;
  }

  if (isProfileError) {
    return (
      <ErrorState
        message={`Error loading profile: ${profileError.message}`}
        onRetry={() => void queryClient.invalidateQueries()}
      />
    );
  }

  if (isContainerMemberError) {
    return (
      <ErrorState
        message={`Error loading membership: ${containerMemberError.message}`}
        onRetry={() => void queryClient.invalidateQueries()}
      />
    );
  }

  if (isRoomMembersError) {
    return (
      <ErrorState
        message={`Error loading chat members: ${roomMembersError.message}`}
        onRetry={() => void queryClient.invalidateQueries()}
      />
    );
  }

  if (isProjectMembersError) {
    return (
      <ErrorState
        message={`Error loading project members: ${projectMembersError.message}`}
        onRetry={() => void queryClient.invalidateQueries()}
      />
    );
  }

  if (isAllMessagesError) {
    return (
      <ErrorState
        message={`Error loading messages: ${allMessagesError.message}`}
        onRetry={() => void queryClient.invalidateQueries()}
      />
    );
  }

  if (isRoomError) {
    return (
      <ErrorState
        message={`Error loading chat: ${roomError.message}`}
        onRetry={() => void queryClient.invalidateQueries()}
      />
    );
  }

  if (!roomContainer) {
    return (
      <ErrorState message="Chat not found." onRetry={() => void queryClient.invalidateQueries()} />
    );
  }

  if (!profile) {
    return (
      <ErrorState
        message="Could not find profile."
        onRetry={() => void queryClient.invalidateQueries()}
      />
    );
  }

  if (!containerMemberId || !aiMembers) {
    return (
      <ErrorState
        message="Are you sure you are a member of this workspace?"
        onRetry={() => void queryClient.invalidateQueries()}
      />
    );
  }

  // Participating personas (CR-25 follow-up): all project personas when the room
  // shares them (is_ai_shared), otherwise the explicit room AI members. Since
  // CR-177 a CHECK forbids is_ai_shared on rooms, so the first branch is always
  // false; it is kept until a follow-up removes the room-side fold-in. Single
  // source for the @mention list, the AI-author rendering, the default-persona
  // marker in the members dialog and the reply chip below.
  // Stays undefined while the project roster loads rather than falling back to
  // the room members: in an is_ai_shared room that fallback would resolve a
  // different persona for one render and flash the wrong reply chip. (The
  // loading guard above already blocks on isProjectMembersLoading, so this is
  // belt-and-braces.)
  const participatingAiMembers = roomContainer.is_ai_shared ? projectAiMembers : aiMembers;
  const isAiChat = roomContainer.container_type === "room";
  // CR-149: the split control replaces the old breadcrumb link to the CR — one
  // affordance, not two. The helper holds the four reasons not to show it.
  const showLinkedCrControl = shouldShowLinkedCrControl({
    linkedCrId,
    isAiChat,
    changeRequestsEnabled: isProjectFeatureEnabled(project, "show_change_requests"),
    workspaceSlug: slug,
    projectSlug,
  });
  // CR-67/CR-97: a message room's DEFAULT title is derived — "All project
  // members" when shared, else its participants (Slack-style). It is shown
  // through the editable title's placeholder until a user types a title of
  // their own; the stored name then wins everywhere (resolveMessageRoomTitle).
  const isMessageRoom = roomContainer.container_type === "message_room";
  const messageRoomDefaultTitle = isMessageRoom
    ? resolveMessageRoomDefaultTitle(
        roomContainer,
        getMessageRoomTitle((roomMembers ?? []).map(memberRowToParticipant), userId),
        { allProjectMembers: "All project members", untitled: "Untitled message" },
      )
    : "";
  // Resolved from the same list — and with the same rule — that useRealtimeChat
  // routes with, so the chip and the routing cannot disagree about who answers
  // an un-mentioned message (CR-46/CR-49).
  const effectiveDefaultAiPersonaId =
    getActivePersona(participatingAiMembers ?? [], roomContainer.default_ai_persona_id)
      ?.ai_persona_id ?? null;

  // CR-171: fills the sized column ProjectLayout gives chat routes (min-h-0 lets it shrink
  // under the credit banner), so the chat's own message list is the scroller and its
  // sticky composer floats at the bottom of the viewport.
  return (
    <div className="flex min-h-0 w-full flex-1 flex-col">
      <div className="border-border flex shrink-0 items-center justify-between gap-4 border-b px-6 py-4">
        <div className="flex min-w-0 flex-1 flex-col">
          {isMessageRoom ? (
            <TitleInputWithLink
              type="text"
              value={title}
              onChange={(e: ChangeEvent<HTMLInputElement>) => onTitleChange(e.target.value)}
              onFocus={onTitleFocus}
              onBlur={onTitleBlur}
              onDebounce={handleTitleDebounce}
              // No caller className: the component already owns the sizing,
              // and a caller `placeholder-*` colour would win over its
              // `placeholder-transparent` and paint the native placeholder
              // under the overlay.
              placeholder={messageRoomDefaultTitle}
              placeholderAsTitle
              // The page's heading is this input; name it by the title it
              // shows, not by the placeholder (which reads as the default even
              // once a title has been typed).
              aria-label={title.trim() || messageRoomDefaultTitle}
              // rename_message_room rejects longer names (22023).
              maxLength={200}
              linkAriaLabel="Copy link to message"
              data-testid="message-room-title"
            />
          ) : (
            <TitleInputWithLink
              type="text"
              value={title}
              onChange={(e: ChangeEvent<HTMLInputElement>) => onTitleChange(e.target.value)}
              onFocus={onTitleFocus}
              onBlur={onTitleBlur}
              onDebounce={handleTitleDebounce}
              className="placeholder-muted-foreground w-full border-none bg-transparent text-3xl font-semibold focus:ring-0 focus:outline-none"
              placeholder="Untitled chat"
              linkAriaLabel="Copy link to chat"
            />
          )}
        </div>
        <div className="flex items-center gap-3">
          {(updateChatName.isPending || renameMessageRoom.isPending) && (
            <div className="text-muted-foreground flex items-center gap-2 text-sm">
              <div className="border-muted-foreground h-4 w-4 animate-spin rounded-full border-b-2" />
              <span>Saving...</span>
            </div>
          )}
          {renameMessageRoom.isError && !renameMessageRoom.isPending && (
            <span className="text-destructive text-sm" role="alert">
              Could not rename the message.
            </span>
          )}
          <ContainerMembersAvatarGroup containerId={roomId} projectId={containerId} />
          {showLinkedCrControl && (
            <LinkedCrControl
              currentCrId={linkedCrId}
              projectId={containerId}
              workspaceSlug={slug!}
              projectSlug={projectSlug!}
              surface={{ type: "chat", id: roomId, projectId: containerId }}
            />
          )}
        </div>
      </div>
      <div className="min-h-0 flex-1">
        <FigmaContextScope value={figmaScope}>
          <RealtimeChat
            key={roomId}
            roomId={roomId}
            roomName={roomContainer.name}
            scrollToMessageId={messageId}
            initialMessage={draft}
            projectContainerId={containerId}
            isShared={roomContainer.is_shared}
            allMessages={displayedMessages || []}
            aiMembers={participatingAiMembers}
            roomContainerType={
              roomContainer.container_type === "message_room" ? "message_room" : "room"
            }
            defaultAiPersonaId={effectiveDefaultAiPersonaId}
            profile={profile}
            onSendMessage={onSendMessage}
            onSendAskUserAnswer={sendAskUserAnswer}
            onResetSendError={resetSendError}
            isSending={isSending}
            isAiResponding={isAiResponding}
            isAiTurnActive={isAiTurnActive}
            isStoppingAiTurn={isStoppingAiTurn}
            onStopAiTurns={onStopAiTurns}
            sendError={sendError}
            creditWarning={creditWarning}
            failedAiTurns={failedAiTurns}
            onRetryAiTurn={retryAiTurn}
            workspaceSlug={slug}
            canManageCredits={canManageCredits}
          />
        </FigmaContextScope>
      </div>
    </div>
  );
}
